import { HttpContext, httpResource, HttpResourceRef } from '@angular/common/http';
import { computed, Service, Signal } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY } from '../../../core/http/http-context-tokens';
import { normalizarTexto, slugDeServico } from '../../../shared/utils/slug.util';
import { BuscaPublicaResponse, ItemBuscaPublicaResponse, ResultadoBusca } from '../models/busca.model';
import { formatarDataNoticia } from '../noticia/data-noticia.pipe';

export const RECURSO_BUSCA = `${environment.apiUrl}/sai/cartaservico/publico/busca`;

/** A tela pede o máximo da rota de uma vez e pagina no cliente. */
export const LIMITE_RESULTADO = 50;

/** O mínimo que a rota aceita; abaixo disso a tela nem consulta. */
export const TERMO_MINIMO = 2;

const DESCRICAO_PADRAO = 'Descrição não informada.';

/** O erro fica local: a tela mostra o aviso de falha ela mesma, sem o modal do interceptor. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

export interface ResultadoBuscaPublica {
  readonly itens: Signal<readonly ResultadoBusca[]>;
  /** Tudo o que casou na API, mesmo além dos 50 carregados. */
  readonly total: Signal<number>;
  readonly carregando: Signal<boolean>;
  readonly falhou: Signal<boolean>;
  recarregar(): void;
}

/** Verdadeiro quando o termo aparado tem o mínimo que a rota aceita. */
export function termoValido(termo: string): boolean {
  return termo.trim().length >= TERMO_MINIMO;
}

/** O título começa com o termo, sem caixa nem acento — a régua do selo "Mais relevante". */
export function comecaComOTermo(titulo: string, termo: string): boolean {
  const alvo = normalizarTexto(termo);

  return alvo.length > 0 && normalizarTexto(titulo).startsWith(alvo);
}

/**
 * O item da API vira card da tela.
 *
 * O link é decisão de apresentação, não dado da API: serviço e categoria pelo slug do nome
 * (a mesma regra do catálogo, que a página de destino usa para resolver o código), e a
 * notícia pelo slug gravado. O serviço devolvido pela busca está, por construção, em
 * categoria publicável — então o `servicoExisteGuard` aceita a rota.
 */
export function paraResultado(item: ItemBuscaPublicaResponse, termo: string): ResultadoBusca {
  const base = {
    titulo: item.titulo,
    descricao: item.descricao?.trim() || DESCRICAO_PADRAO,
    maisRelevante: comecaComOTermo(item.titulo, termo),
  };

  switch (item.tipo) {
    case 'Servico':
      return {
        ...base,
        id: `servico-${item.codigo}`,
        canal: 'servicos',
        link: `/${slugDeServico(item.titulo)}`,
        destaques: item.tiposAtendimento,
      };
    case 'CategoriaServico':
      return {
        ...base,
        id: `categoria-${item.codigo}`,
        canal: 'categorias',
        link: `/servicos/${slugDeServico(item.titulo)}`,
        destaques: ['Categoria de serviços'],
      };
    case 'Noticia':
      return {
        ...base,
        id: `noticia-${item.codigo}`,
        canal: 'noticias',
        link: `/noticias/${item.slug ?? ''}`,
        destaques: [formatarDataNoticia(item.data)].filter(Boolean),
      };
  }
}

/** O valor do recurso, ou nulo em erro: `value()` lança quando o recurso está em erro. */
function valor(recurso: HttpResourceRef<ResponseApi<BuscaPublicaResponse> | undefined>): BuscaPublicaResponse | null {
  if (recurso.error() !== undefined) return null;

  return recurso.value()?.data ?? null;
}

/**
 * A busca pública do portal. O portal não envia o órgão: o back-end o resolve pelo host.
 *
 * `buscar` cria um `httpResource` e precisa ser chamado num contexto de injeção (o
 * inicializador de campo do componente): o recurso pertence à tela, refaz a consulta quando o
 * termo muda e fica ocioso — sem chamar a API — enquanto o termo não tiver 2 caracteres.
 */
@Service()
export class BuscaPublicaService {
  buscar(termo: () => string): ResultadoBuscaPublica {
    const recurso = httpResource<ResponseApi<BuscaPublicaResponse>>(() => {
      const atual = termo().trim();

      return termoValido(atual)
        ? { url: RECURSO_BUSCA, params: { termo: atual, limite: String(LIMITE_RESULTADO) }, context: ERRO_LOCAL() }
        : undefined;
    });

    return {
      itens: computed(() => (valor(recurso)?.itens ?? []).map((item) => paraResultado(item, termo()))),
      total: computed(() => valor(recurso)?.total ?? 0),
      carregando: computed(() => recurso.isLoading()),
      falhou: computed(() => recurso.error() !== undefined),
      recarregar: () => recurso.reload(),
    };
  }
}
